/**
 * Tooltip — renders via portal to avoid overflow clipping.
 * For Media Devoted | Created By Ciro Autiero
 */

import {
  type FC,
  type FocusEvent,
  type ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';

import { cn } from '@/lib/utils';

const VIEWPORT_MARGIN = 16;
/** ~42rem cap, aligned with SIEM `max-w-[min(42rem,calc(100vw-2rem))]`. */
const TOOLTIP_MAX_WIDTH_CAP_PX = 672;

function clampTopTooltipLeft(trigger: DOMRect): number {
  const vw = window.innerWidth;
  const maxTooltipWidth = Math.min(
    TOOLTIP_MAX_WIDTH_CAP_PX,
    Math.max(160, vw - 2 * VIEWPORT_MARGIN),
  );
  const centerX = trigger.left + trigger.width / 2;
  const half = maxTooltipWidth / 2;
  const minLeft = VIEWPORT_MARGIN + half;
  const maxLeft = vw - VIEWPORT_MARGIN - half;
  return Math.min(Math.max(centerX, minLeft), maxLeft);
}

interface TooltipProps {
  content: ReactNode;
  children: ReactNode;
  className?: string;
  contentClassName?: string;
  side?: 'top' | 'right' | 'bottom';
  /** Cursor can move onto tooltip; content may scroll (short hide delay). */
  interactive?: boolean;
}

export const Tooltip: FC<TooltipProps> = ({
  content,
  children,
  className,
  contentClassName,
  side = 'top',
  interactive = false,
}) => {
  const triggerRef = useRef<HTMLSpanElement>(null);
  const leaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  const clearLeaveTimer = () => {
    if (leaveTimerRef.current) {
      clearTimeout(leaveTimerRef.current);
      leaveTimerRef.current = null;
    }
  };

  useEffect(() => () => clearLeaveTimer(), []);

  if (!content) return <>{children}</>;

  const show = () => {
    clearLeaveTimer();
    const el = triggerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    if (side === 'right') {
      setPos({ top: r.top + r.height / 2, left: r.right + 8 });
      return;
    }
    if (side === 'bottom') {
      setPos({
        top: r.bottom + 8,
        left: clampTopTooltipLeft(r),
      });
      return;
    }
    setPos({
      top: r.top - 8,
      left: clampTopTooltipLeft(r),
    });
  };

  const scheduleHide = () => {
    clearLeaveTimer();
    if (!interactive) {
      setPos(null);
      return;
    }
    leaveTimerRef.current = setTimeout(() => setPos(null), 200);
  };

  // Show on KEYBOARD focus only — not when a control is focused
  // programmatically (e.g. a dialog auto-focusing its first button on open),
  // which would otherwise pop the tooltip mid-screen anchored to a stale rect.
  const handleFocus = (event: FocusEvent<HTMLSpanElement>) => {
    const target = event.target as HTMLElement | null;
    try {
      if (target?.matches(':focus-visible')) show();
    } catch {
      show();
    }
  };

  const transform =
    side === 'right'
      ? 'translateY(-50%)'
      : side === 'bottom'
        ? 'translate(-50%, 0)'
        : 'translate(-50%, -100%)';

  return (
    <span
      ref={triggerRef}
      className={cn('relative inline-flex', className)}
      onMouseEnter={show}
      onMouseLeave={scheduleHide}
      onFocus={handleFocus}
      onBlur={scheduleHide}
    >
      {children}
      {pos !== null &&
        createPortal(
          <span
            role="tooltip"
            className={cn(
              'fixed z-[9999]',
              interactive ? 'pointer-events-auto' : 'pointer-events-none',
            )}
            style={{ top: pos.top, left: pos.left, transform }}
            onMouseEnter={interactive ? clearLeaveTimer : undefined}
            onMouseLeave={interactive ? scheduleHide : undefined}
          >
            <span
              className={cn(
                'block max-w-xs min-w-[160px] whitespace-normal rounded-md px-4 py-3 text-xs leading-relaxed shadow-lg',
                'bg-foreground text-background',
                contentClassName,
              )}
            >
              {content}
            </span>
          </span>,
          document.body,
        )}
    </span>
  );
};

export default Tooltip;
